import { useState, useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@pantry/ui';
import { logger } from '../../lib/logger';
import {
  GlobeAltIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import {
  useContributeTranslation,
  useCurrentUser,
  materializeExternalProduct,
  type Product,
} from '@pantry/api-client';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '@pantry/shared';
import { useSelectedHousehold } from '../../contexts/HouseholdContext';
import { toast } from 'sonner';
import { cn } from '@pantry/ui';

interface ProductInternationalNamesProps {
  product: Product;
  /** Variant: 'inline' shows expandable section, 'dialog' shows button that opens dialog */
  variant?: 'inline' | 'dialog';
  /** Called when an external product is materialized (saved locally) during translation edit */
  onProductMaterialized?: (product: Product) => void;
}

/**
 * Displays all international names for a product and allows users
 * to edit names in their authorized languages (user language and/or household language).
 */

export function ProductInternationalNames({
  product,
  variant = 'inline',
  onProductMaterialized,
}: ProductInternationalNamesProps) {
  const [isMaterializing, setIsMaterializing] = useState(false);
  const { t } = useLingui();
  const [isExpanded, setIsExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLang, setEditingLang] = useState<LanguageCode | null>(null);
  const [editValue, setEditValue] = useState('');
  const [addingLang, setAddingLang] = useState<LanguageCode | null>(null);
  const [addValue, setAddValue] = useState('');

  const { data: user } = useCurrentUser();
  const { selectedHousehold } = useSelectedHousehold();
  const contributeTranslation = useContributeTranslation();

  // Determine which languages the user can edit
  const editableLanguages = useMemo(() => {
    const languages = new Set<LanguageCode>();

    // User's personal language
    if (user?.language) {
      languages.add(user.language as LanguageCode);
    }

    // Household's language
    if (selectedHousehold?.language) {
      languages.add(selectedHousehold.language as LanguageCode);
    }

    // If no preferences set, default to English
    if (languages.size === 0) {
      languages.add('en');
    }

    return languages;
  }, [user?.language, selectedHousehold?.language]);

  // Get all names from the product
  const productNames = useMemo(() => {
    const names: {
      code: LanguageCode;
      name: string;
      languageName: string;
      isUserContribution: boolean;
    }[] = [];

    if (product.names) {
      // Get user contributions first (they have priority)
      const userNames = (product.names as { _user?: Record<string, string> })._user || {};

      for (const [code, value] of Object.entries(product.names)) {
        // Skip the _user key
        if (code === '_user') continue;

        const langInfo = SUPPORTED_LANGUAGES.find((l) => l.code === code);
        if (!langInfo) continue;

        // Value can be string or nested object - extract the actual name
        let nameValue: string | undefined;
        if (typeof value === 'string') {
          nameValue = value;
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
          // Nested object - try to get the language-specific value
          nameValue = (value as Record<string, string>)[code] || Object.values(value)[0];
        }

        // Check if there's a user contribution that overrides this
        const userContribution = userNames[code];
        const finalName = userContribution || nameValue;

        if (finalName) {
          names.push({
            code: code as LanguageCode,
            name: finalName,
            languageName: langInfo.name,
            isUserContribution: !!userContribution,
          });
        }
      }

      // Add any user contributions for languages not in the main names
      for (const [code, name] of Object.entries(userNames)) {
        if (!names.some((n) => n.code === code)) {
          const langInfo = SUPPORTED_LANGUAGES.find((l) => l.code === code);
          if (langInfo && name) {
            names.push({
              code: code as LanguageCode,
              name,
              languageName: langInfo.name,
              isUserContribution: true,
            });
          }
        }
      }
    }

    // Sort: editable languages first, then alphabetically
    names.sort((a, b) => {
      const aEditable = editableLanguages.has(a.code);
      const bEditable = editableLanguages.has(b.code);
      if (aEditable && !bEditable) return -1;
      if (!aEditable && bEditable) return 1;
      return a.languageName.localeCompare(b.languageName);
    });

    return names;
  }, [product.names, editableLanguages]);

  // Languages that can be added (editable but not yet present)
  const addableLanguages = useMemo(() => {
    return Array.from(editableLanguages).filter(
      (code) => !productNames.some((n) => n.code === code)
    );
  }, [editableLanguages, productNames]);

  // Materialize external product if needed, returns product ID
  const resolveProductId = async (): Promise<string> => {
    if (product.id) return product.id;
    if (!product.barcode) throw new Error('No barcode for materialization');
    setIsMaterializing(true);
    try {
      const materialized = await materializeExternalProduct(product.barcode);
      onProductMaterialized?.(materialized);
      return materialized.id;
    } finally {
      setIsMaterializing(false);
    }
  };

  const handleStartEdit = (code: LanguageCode, currentName: string) => {
    setEditingLang(code);
    setEditValue(currentName);
  };

  const handleCancelEdit = () => {
    setEditingLang(null);
    setEditValue('');
  };

  const handleSaveEdit = async () => {
    if (!editingLang || !editValue.trim()) return;

    try {
      const productId = await resolveProductId();
      await contributeTranslation.mutateAsync({
        productId,
        language: editingLang,
        name: editValue.trim(),
      });
      const langName = SUPPORTED_LANGUAGES.find((l) => l.code === editingLang)?.name;
      toast.success(t`${langName} name updated`);
      setEditingLang(null);
      setEditValue('');
    } catch (err) {
      logger.error('Failed to update translation', err, {
        productId: product.id,
        language: editingLang,
      });
      toast.error(t`Failed to save translation`);
    }
  };

  const handleStartAdd = (code: LanguageCode) => {
    setAddingLang(code);
    setAddValue('');
  };

  const handleCancelAdd = () => {
    setAddingLang(null);
    setAddValue('');
  };

  const handleSaveAdd = async () => {
    if (!addingLang || !addValue.trim()) return;

    try {
      const productId = await resolveProductId();
      await contributeTranslation.mutateAsync({
        productId,
        language: addingLang,
        name: addValue.trim(),
      });
      const langName = SUPPORTED_LANGUAGES.find((l) => l.code === addingLang)?.name;
      toast.success(t`${langName} name added`);
      setAddingLang(null);
      setAddValue('');
    } catch (err) {
      logger.error('Failed to add translation', err, {
        productId: product.id,
        language: addingLang,
      });
      toast.error(t`Failed to add translation`);
    }
  };

  // Don't show if product has no names object
  if (!product.names || Object.keys(product.names).length === 0) {
    // Still show add option if user can add translations
    if (addableLanguages.length === 0) {
      return null;
    }
  }

  const content = (
    <div className="space-y-3">
      {/* Existing names */}
      {productNames.length > 0 && (
        <div className="space-y-2">
          {productNames.map(({ code, name, languageName, isUserContribution }) => {
            const canEdit = editableLanguages.has(code);
            const isEditing = editingLang === code;

            return (
              <div
                key={code}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-xl border transition-colors',
                  canEdit ? 'border-primary/20 bg-primary/5' : 'border-border/50 bg-muted/30'
                )}
              >
                {isEditing ? (
                  // Edit mode
                  <div className="flex-1 flex items-center gap-2">
                    <Input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="flex-1 h-8 rounded-lg text-sm"
                      autoFocus
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={handleSaveEdit}
                      disabled={
                        contributeTranslation.isPending || isMaterializing || !editValue.trim()
                      }
                    >
                      <CheckIcon className="h-4 w-4 text-fresh" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={handleCancelEdit}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  // Display mode
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-muted-foreground uppercase">
                          {code}
                        </span>
                        <span className="text-xs text-muted-foreground">{languageName}</span>
                        {isUserContribution && (
                          <span className="text-xs text-fresh bg-fresh/10 px-1.5 py-0.5 rounded">
                            <Trans>user</Trans>
                          </span>
                        )}
                        {canEdit && (
                          <span className="text-xs text-primary">
                            <Trans>(editable)</Trans>
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium truncate">{name}</p>
                    </div>
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 flex-shrink-0"
                        onClick={() => handleStartEdit(code, name)}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add new translation */}
      {addableLanguages.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            <Trans>Add a name in your language:</Trans>
          </p>
          {addableLanguages.map((code) => {
            const langInfo = SUPPORTED_LANGUAGES.find((l) => l.code === code);
            const isAdding = addingLang === code;

            return (
              <div
                key={code}
                className="flex items-center gap-3 p-3 rounded-xl border border-dashed border-primary/30 bg-primary/5"
              >
                {isAdding ? (
                  <div className="flex-1 flex items-center gap-2">
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs">{langInfo?.name}</Label>
                      <Input
                        value={addValue}
                        onChange={(e) => setAddValue(e.target.value)}
                        placeholder={t`Enter name in ${langInfo?.name}`}
                        className="h-8 rounded-lg text-sm"
                        autoFocus
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={handleSaveAdd}
                      disabled={
                        contributeTranslation.isPending || isMaterializing || !addValue.trim()
                      }
                    >
                      <CheckIcon className="h-4 w-4 text-fresh" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={handleCancelAdd}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex-1">
                      <span className="text-xs font-medium text-muted-foreground uppercase">
                        {code}
                      </span>
                      <span className="text-xs text-muted-foreground ml-2">{langInfo?.name}</span>
                      <p className="text-sm text-muted-foreground italic">
                        <Trans>No translation yet</Trans>
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 rounded-lg"
                      onClick={() => handleStartAdd(code)}
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      <Trans>Add</Trans>
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Info text */}
      <p className="text-xs text-muted-foreground">
        <Trans>
          You can edit names in{' '}
          {Array.from(editableLanguages)
            .map((code) => SUPPORTED_LANGUAGES.find((l) => l.code === code)?.name)
            .join(' and ')}{' '}
          based on your user and household settings.
        </Trans>
      </p>
    </div>
  );

  if (variant === 'dialog') {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          className="rounded-xl"
          onClick={() => setDialogOpen(true)}
        >
          <GlobeAltIcon className="h-4 w-4 mr-2" />
          <Trans>International Names</Trans>
        </Button>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                  <GlobeAltIcon className="h-4 w-4 text-primary" />
                </div>
                <Trans>International Names</Trans>
              </DialogTitle>
              <DialogDescription>
                <Trans>View and edit product names in different languages.</Trans>
              </DialogDescription>
            </DialogHeader>
            {content}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Inline expandable variant
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full"
      >
        <GlobeAltIcon className="h-4 w-4" />
        <span>
          <Trans>International Names ({productNames.length})</Trans>
        </span>
        {isExpanded ? (
          <ChevronUpIcon className="h-4 w-4 ml-auto" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 ml-auto" />
        )}
      </button>
      {isExpanded && (
        <div className="animate-in fade-in slide-in-from-top-2 duration-200">{content}</div>
      )}
    </div>
  );
}
